import proxy from "api/proxy";
import { toDatetimeInput, toDatetimeValue } from "./datetime";

const RELATION_TYPES = ["record", "records"];

function isFilled(value) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function getEmptyValue(field) {
  if (field.type === "records") return [];
  if (field.type === "record") return null;
  if (field.type === "boolean") return false;
  return "";
}

function getDefaults(fields) {
  const form = {};
  fields.forEach((field) => {
    form[field.name] = field.default ?? getEmptyValue(field);
  });
  return form;
}

function toFormValue(field, record) {
  if (RELATION_TYPES.includes(field.type)) {
    return record[`${field.name}.`] ?? getEmptyValue(field);
  }
  const value = record[field.name];
  if (field.type === "datetime") return toDatetimeInput(value);
  if (field.type === "boolean") return Boolean(value);
  return value ?? getEmptyValue(field);
}

function toForm(fields, record) {
  const form = {};
  fields.forEach((field) => {
    form[field.name] = toFormValue(field, record);
  });
  return form;
}

function getReadFields(fields) {
  return fields.map((field) =>
    RELATION_TYPES.includes(field.type) ? `${field.name}.rec_name` : field.name,
  );
}

function getRecordIds(records) {
  return records.map((record) => record.id);
}

function getManyActions(value, previous) {
  const ids = getRecordIds(value);
  const previousIds = getRecordIds(previous);
  const added = ids.filter((id) => !previousIds.includes(id));
  const removed = previousIds.filter((id) => !ids.includes(id));
  const actions = [];
  if (added.length) actions.push(["add", added]);
  if (removed.length) actions.push(["remove", removed]);
  return actions;
}

function toSaveValue(field, value) {
  if (field.type === "record") return value?.id ?? null;
  if (field.type === "datetime") return toDatetimeValue(value);
  if (field.type === "boolean") return value;
  if (field.type === "number") return value === "" ? null : Number(value);
  const text = String(value).trim();
  return text === "" ? null : text;
}

function isSameValue(field, value, previous) {
  if (field.type === "record") return value?.id === previous?.id;
  return value === previous;
}

function getValues(fields, form, saved) {
  const values = {};
  fields.forEach((field) => {
    const value = form[field.name];
    const previous = saved[field.name];
    if (field.type === "records") {
      const actions = getManyActions(value, previous);
      if (actions.length) values[field.name] = actions;
    } else if (!isSameValue(field, value, previous)) {
      values[field.name] = toSaveValue(field, value);
    }
  });
  return values;
}

function getCreateValues(fields, form) {
  const empty = {};
  fields.forEach((field) => {
    if (field.type !== "boolean") empty[field.name] = getEmptyValue(field);
  });
  return getValues(fields, form, empty);
}

function validateForm(fields, form) {
  for (const field of fields) {
    if (field.required && !isFilled(form[field.name])) {
      return {
        id: "melhous.dash.list.required",
        field: field.label,
      };
    }
    if (field.after && form[field.name]) {
      const start = form[field.after];
      if (!start || form[field.name] < start) {
        return { id: "melhous.dash.list.invalid_end" };
      }
    }
  }
  return null;
}

function getDetail(error) {
  return typeof error === "string" ? error : null;
}

function getCreatedId(data) {
  const created = Array.isArray(data) ? data[0] : data;
  return created?.id ?? created ?? null;
}

async function fetchItems(config, parentId) {
  const domain = [
    [config.parentField, "=", parentId],
    ...(config.domain || []),
  ];
  return await proxy.search(config.model, domain, getReadFields(config.fields));
}

async function saveItem({ config, parentId, recordId, form, saved }) {
  const values = recordId
    ? getValues(config.fields, form, saved)
    : getCreateValues(config.fields, form);
  if (recordId && !Object.keys(values).length) return { id: recordId };
  const storeRec = recordId
    ? { id: recordId, ...values }
    : { ...values, [config.parentField]: parentId };
  const { data, error } = await proxy.saveQuery({
    model: config.model,
    storeRec,
    fieldsNames: ["id"],
  });
  if (error) return { error };
  return { id: recordId ?? getCreatedId(data) };
}

async function deleteChildren(child, recordId) {
  const { data, error } = await proxy.search(
    child.model,
    [[child.parentField, "=", recordId]],
    ["id"],
  );
  if (error) return { error };
  if (!data?.length) return {};
  return await proxy.remove({ model: child.model, ids: getRecordIds(data) });
}

async function deleteItem(config, recordId) {
  if (config.children) {
    const { error } = await deleteChildren(config.children, recordId);
    if (error) return { error };
  }
  return await proxy.remove({ model: config.model, ids: [recordId] });
}

export {
  deleteItem,
  fetchItems,
  getCreatedId,
  getDefaults,
  getDetail,
  saveItem,
  toForm,
  validateForm,
};
